import React from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import CustomModal from "../../components/CustomComponents/modal/customModal";

export const SERVEIQ_DOJO_WEBHOOK_URL =
  "https://possvc.serveiq.co.uk/api/dojo/webhook";

const DOJO_DEVELOPER_PORTAL_URL = "https://developer.dojo.tech";

function DojoWebhookSecretHelpModal({ isOpen, onClose }) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const textColor = isDarkMode ? "#e9ecef" : "#212529";
  const mutedColor = isDarkMode ? "#adb5bd" : "#6c757d";
  const codeBg = isDarkMode ? "#1a1d20" : "#f1f3f5";
  const noteBg = isDarkMode ? "rgba(13, 110, 253, 0.12)" : "rgba(13, 110, 253, 0.08)";
  const warnBg = isDarkMode ? "rgba(255, 193, 7, 0.12)" : "rgba(255, 193, 7, 0.15)";

  const stepStyle = {
    color: textColor,
    marginBottom: "1rem",
    lineHeight: 1.55,
    fontSize: "0.95rem",
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="How to get the Secondary Key (Webhook secret)"
      subtitle="Dojo payment gateway — step-by-step setup in the Dojo Developer Portal"
      showButton={false}
      isCentered={true}
      modalSize="modal-lg"
      maxHeight="85vh"
      zIndexBase={10050}
      themeColor={themeColor}
    >
      <div style={{ color: textColor }}>
        <div
          style={{
            background: noteBg,
            borderLeft: `4px solid ${themeColor}`,
            padding: "12px 14px",
            borderRadius: "8px",
            marginBottom: "1.25rem",
            fontSize: "0.9rem",
          }}
        >
          <strong>What is the Secondary Key?</strong>
          <p className="mb-0 mt-1" style={{ color: mutedColor }}>
            It is the <strong>webhook signing secret</strong> from Dojo. ServeIQ uses it to
            verify that payment notifications sent to our server are genuine. This is
            <strong> not</strong> the same as your API secret key (Primary Key).
          </p>
        </div>

        <p style={{ color: mutedColor, fontSize: "0.875rem", marginBottom: "1rem" }}>
          Use the <strong>same Dojo account</strong> (sandbox or live) as the API key you entered
          in Primary Key. Each company registers the ServeIQ webhook URL once in their own Dojo
          portal and copies their own signing secret here.
        </p>

        <h6 className="fw-semibold mb-3" style={{ color: textColor }}>
          Step-by-step instructions
        </h6>

        <ol className="ps-3 mb-0" style={{ listStylePosition: "outside" }}>
          <li style={stepStyle}>
            <strong>Sign in to the Dojo Developer Portal</strong>
            <br />
            <span style={{ color: mutedColor }}>
              Open{" "}
              <a
                href={DOJO_DEVELOPER_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: themeColor }}
              >
                developer.dojo.tech
              </a>{" "}
              and log in with the account that owns your Dojo API keys.
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Select the correct location account</strong>
            <br />
            <span style={{ color: mutedColor }}>
              If your business has multiple locations in Dojo, choose the same location account
              you used when creating the <strong>Primary Key</strong> (API secret key).
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Open Webhooks</strong>
            <br />
            <span style={{ color: mutedColor }}>
              In the left menu, go to <strong>Webhooks</strong>, then click{" "}
              <strong>Add endpoint</strong> (or <strong>+ Add endpoint</strong>).
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Enter the ServeIQ webhook URL</strong>
            <br />
            <span style={{ color: mutedColor }}>
              In <strong>Endpoint URL</strong>, paste this address exactly (HTTPS, no trailing
              slash):
            </span>
            <div
              className="mt-2 p-2 rounded font-monospace small user-select-all"
              style={{
                background: codeBg,
                color: textColor,
                wordBreak: "break-all",
                border: isDarkMode ? "1px solid #495057" : "1px solid #dee2e6",
              }}
            >
              {SERVEIQ_DOJO_WEBHOOK_URL}
            </div>
          </li>

          <li style={stepStyle}>
            <strong>Choose the event to listen for</strong>
            <br />
            <span style={{ color: mutedColor }}>
              Under events, select only{" "}
              <strong>payment_intent.status_updated</strong> (payment status updates). You do
              not need other event types for online ordering or reservations.
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Save the endpoint</strong>
            <br />
            <span style={{ color: mutedColor }}>
              Click <strong>Add endpoint</strong> (or <strong>Save</strong>) so Dojo can send
              notifications to ServeIQ when a customer pays.
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Generate and activate a webhook secret</strong>
            <br />
            <span style={{ color: mutedColor }}>
              Open your new endpoint’s <strong>Secrets</strong> section. Create a new secret,
              <strong> activate</strong> it, then copy the <strong>signing secret</strong> value
              (sometimes shown as “Secret value”). Keep it private — treat it like a password.
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Paste into Secondary Key in ServeIQ</strong>
            <br />
            <span style={{ color: mutedColor }}>
              Return to this Payment Gateway form, paste the copied value into the{" "}
              <strong>Secondary Key</strong> field, set <strong>Status</strong> to Active if
              applicable, and click <strong>Add</strong> or <strong>Update</strong>.
            </span>
          </li>

          <li style={stepStyle}>
            <strong>Confirm Primary Key and Host URL</strong>
            <br />
            <span style={{ color: mutedColor }}>
              <strong>Primary Key:</strong> your Dojo secret API key (
              <code>sk_sandbox_...</code> for testing or <code>sk_prod_...</code> for live).
              <br />
              <strong>Host URL:</strong> typically <code>https://api.dojo.tech</code> unless Dojo
              gave you a different API base URL.
            </span>
          </li>
        </ol>

        <div
          className="mt-3"
          style={{
            background: warnBg,
            padding: "12px 14px",
            borderRadius: "8px",
            fontSize: "0.875rem",
            color: textColor,
          }}
        >
          <strong>Sandbox vs live</strong>
          <p className="mb-0 mt-1" style={{ color: mutedColor }}>
            Sandbox and production use separate API keys and webhook secrets. When you go live,
            repeat these steps in your <strong>production</strong> Dojo account and update both
            Primary Key and Secondary Key in ServeIQ.
          </p>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-switchery px-4"
            onClick={onClose}
            style={{
              backgroundColor: themeColor,
              border: `1px solid ${themeColor}`,
              color: "#fff",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </CustomModal>
  );
}

export default DojoWebhookSecretHelpModal;
